<template>
	<div ref="containerRef" class="relative flex items-center">
		<button type="button" @click="toggleSearch">
			<MagnifyingGlassIcon class="h-6 w-6" />
			<span class="sr-only">{{ $t("Open search") }}</span>
		</button>
		<div class="relative">
			<form @submit.prevent="goToSearchPage(searchQuery)">
				<input
					ref="inputRef"
					v-model="searchQuery"
					type="text"
					class="border-x-0 border-t-0 border-b border-solid bg-transparent py-2 text-sm placeholder:text-sm outline-none transition-all duration-300 ease-in-out"
					:class="`${isSearchOpen ? 'w-[200px] px-2 opacity-100' : 'w-0 px-0 opacity-0'} ${isTransparentHeader ? 'text-white placeholder:text-white border-white' : 'text-primary placeholder:text-primary-gray-500 border-primary'}`"
					name="search_products"
					:placeholder="$t('Search')"
					@focus="isFocused = true"
					@keydown="onInputKeydown"
				/>
			</form>

			<div
				v-if="isSearchOpen && isFocused && searchQuery.trim().length >= 2"
				class="absolute right-0 top-full z-40 mt-2 w-[320px] overflow-hidden rounded border border-gray-200 bg-white shadow-lg"
			>
				<div
					v-for="group in suggestionGroups"
					:key="group.key"
					class="border-t border-gray-100 first:border-t-0"
				>
					<p
						class="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500"
					>
						{{ group.label }}
					</p>
					<NuxtLink
						v-for="item in group.items"
						:key="item.id"
						:to="resolveSuggestionTo(item)"
						class="flex items-center gap-3 px-3 py-2 text-sm text-gray-800 hover:bg-gray-100"
						:class="{ 'bg-gray-100': activeSuggestionId === item.id }"
						@mouseenter="setActiveSuggestion(item.id)"
						@click="openSuggestion(item)"
					>
						<div
							class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded bg-gray-100"
						>
							<NuxtImg
								v-if="item.image"
								:src="item.image"
								:alt="item.label"
								class="h-full w-full object-cover"
								width="40"
								height="40"
							/>
							<MagnifyingGlassIcon v-else class="h-4 w-4 text-primary" />
						</div>
						<div class="min-w-0">
							<p class="truncate text-sm font-medium">{{ item.label }}</p>
							<p class="text-xs uppercase text-gray-500">
								{{ formatKindLabel(item.kind) }}
							</p>
						</div>
					</NuxtLink>
				</div>
				<button
					type="button"
					class="block w-full border-t border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-100"
					@click="goToSearchPage(searchQuery)"
				>
					{{ $t("search_for_query", { query: searchQuery.trim() }) }}
				</button>
				<p v-if="isLoadingSuggestions" class="px-3 py-2 text-sm text-gray-500">
					{{ $t("loading_suggestions") }}
				</p>
				<p
					v-if="!isLoadingSuggestions && flatSuggestions.length === 0"
					class="px-3 py-2 text-sm text-gray-500"
				>
					{{ $t("no_suggestions_found") }}
				</p>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { MagnifyingGlassIcon } from "@heroicons/vue/24/solid";
import type {
	SearchSuggestionGroup,
	SearchSuggestionItem
} from "@nwt/norce-pwa/dist/runtime/types/api/searchEngine/searchSuggest";
import { useRoute } from "nuxt/app";
import { stepSuggestionIndex } from "~/layers/base/utils/suggestionNavigation";

interface Props {
	isTransparentHeader?: boolean;
}

defineProps<Props>();

const localePath = useLocalePath();
const route = useRoute();
const { localeProperties, t } = useI18n();
const { searchSuggestions } = useNorcePwa();
const searchQuery = ref<string>((route.query.q as string) || "");
const suggestionGroups = ref<SearchSuggestionGroup[]>([]);
const isSearchOpen = ref(false);
const isFocused = ref(false);
const isLoadingSuggestions = ref(false);
const activeIndex = ref(-1);
const containerRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);

const toPath = (value?: string) => {
	if (!value) {
		return "/";
	}
	if (value.startsWith("http://") || value.startsWith("https://")) {
		try {
			const url = new URL(value);
			return `${url.pathname}${url.search}`;
		} catch {
			return "/";
		}
	}
	return value.startsWith("/") ? value : `/${value}`;
};

const flatSuggestions = computed(() =>
	suggestionGroups.value.flatMap((group) => group.items)
);

const activeSuggestionId = computed(
	() => flatSuggestions.value[activeIndex.value]?.id || ""
);

const formatKindLabel = (kind: SearchSuggestionItem["kind"]) => {
	switch (kind) {
		case "category":
			return t("Category");
		case "query":
			return t("Search");
		case "article":
			return t("Content");
		default:
			return t("Product");
	}
};

const resolveSuggestionTo = (item: SearchSuggestionItem) => {
	if (item.kind === "query") {
		return localePath({
			path: "/search",
			query: { q: item.query || item.label }
		});
	}

	const path = toPath(item.url);
	return path === "/" ? "/" : localePath(path);
};

const setActiveSuggestion = (id: string) => {
	activeIndex.value = flatSuggestions.value.findIndex((item) => item.id === id);
};

const goToSearchPage = (query: string) => {
	const normalized = query.trim();
	if (!normalized) {
		return;
	}

	isFocused.value = false;
	isSearchOpen.value = false;
	activeIndex.value = -1;
	navigateTo(
		localePath({
			path: "/search",
			query: { q: normalized }
		})
	);
};

const openSuggestion = (item: SearchSuggestionItem) => {
	isFocused.value = false;
	isSearchOpen.value = false;
	activeIndex.value = -1;
	navigateTo(resolveSuggestionTo(item));
};

const toggleSearch = async () => {
	isSearchOpen.value = !isSearchOpen.value;
	if (isSearchOpen.value) {
		await nextTick();
		inputRef.value?.focus();
	} else {
		isFocused.value = false;
		activeIndex.value = -1;
	}
};

const onInputKeydown = (event: KeyboardEvent) => {
	if (!isFocused.value || flatSuggestions.value.length === 0) {
		if (event.key === "Escape") {
			isFocused.value = false;
			isSearchOpen.value = false;
		}
		return;
	}

	if (event.key === "ArrowDown") {
		event.preventDefault();
		activeIndex.value = stepSuggestionIndex(
			activeIndex.value,
			flatSuggestions.value.length,
			"next"
		);
		return;
	}

	if (event.key === "ArrowUp") {
		event.preventDefault();
		activeIndex.value = stepSuggestionIndex(
			activeIndex.value,
			flatSuggestions.value.length,
			"previous"
		);
		return;
	}

	if (event.key === "Enter" && activeIndex.value >= 0) {
		event.preventDefault();
		openSuggestion(flatSuggestions.value[activeIndex.value]!);
		return;
	}

	if (event.key === "Escape") {
		event.preventDefault();
		isFocused.value = false;
		isSearchOpen.value = false;
		activeIndex.value = -1;
	}
};

let debounceTimer: ReturnType<typeof setTimeout> | undefined;
watch(searchQuery, (value) => {
	if (debounceTimer) {
		clearTimeout(debounceTimer);
	}

	if (value.trim().length < 2) {
		suggestionGroups.value = [];
		activeIndex.value = -1;
		return;
	}

	debounceTimer = setTimeout(async () => {
		isLoadingSuggestions.value = true;
		try {
			const result = await searchSuggestions({
				query: value.trim(),
				culture: localeProperties.value.iso || "sv-SE"
			});
			suggestionGroups.value = result.groups;
			// No auto-highlight — Enter must submit the query, not open a hit.
			activeIndex.value = -1;
		} catch {
			suggestionGroups.value = [];
			activeIndex.value = -1;
		} finally {
			isLoadingSuggestions.value = false;
		}
	}, 180);
});

watch(
	() => route.query.q,
	(newQuery) => {
		searchQuery.value = (newQuery as string) || "";
		activeIndex.value = -1;
	}
);

watch(
	() => route.fullPath,
	() => {
		isSearchOpen.value = false;
		isFocused.value = false;
		activeIndex.value = -1;
		suggestionGroups.value = [];
	}
);

onMounted(() => {
	document.addEventListener("click", onClickOutside);
});

onBeforeUnmount(() => {
	document.removeEventListener("click", onClickOutside);
});

const onClickOutside = (event: MouseEvent) => {
	if (!containerRef.value) {
		return;
	}

	if (!containerRef.value.contains(event.target as Node)) {
		isFocused.value = false;
		activeIndex.value = -1;
	}
};
</script>
